import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ImageViewer from '../components/ImageViewer';
import './Bugs.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://intranet.nesimltd.co.ke';

// Helper function to construct absolute URLs for attachments
const getAbsoluteUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  
  // If it's already an absolute URL, return as is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }
  
  // If it starts with /, it's a relative path from the backend root
  if (relativeUrl.startsWith('/')) {
    return `${API_BASE}${relativeUrl}`;
  }
  
  // Otherwise, assume it's relative to the uploads directory
  return `${API_BASE}/uploads/support/${relativeUrl}`;
};

export default function BugDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bug, setBug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    async function fetchBug() {
      try {
        const response = await fetch(`${API_BASE}/api/support/bugs/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch bug details');
        }
        
        const data = await response.json();
        console.log('Bug data received:', data);
        console.log('Bug attachmentData:', data.attachmentData);
        console.log('Bug attachment:', data.attachment);
        console.log('Bug has attachment:', !!(data.attachmentData || data.attachment));
        
        // Check if attachmentData has valid URLs
        if (data.attachmentData) {
          console.log('AttachmentData URLs:', {
            compressedUrl: data.attachmentData.compressedUrl,
            originalUrl: data.attachmentData.originalUrl,
            thumbnailUrl: data.attachmentData.thumbnailUrl
          });
          
          // Test the URL construction
          const testUrl = getAbsoluteUrl(data.attachmentData.compressedUrl || data.attachmentData.originalUrl || data.attachment);
          console.log('Constructed absolute URL:', testUrl);
        }
        
        setBug(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchBug();
  }, [id]);

  if (loading) {
    return <div className="loading">Loading bug details...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!bug) {
    return <div className="error">Bug not found</div>;
  }

  return (
    <div className="bug-detail-page">
      <div className="bug-detail-header">
        <button 
          className="back-button"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <h1>Bug Report Details</h1>
      </div>

      <div className="bug-detail-content">
        <div className="bug-main-info">
          <div className="bug-title-section">
            <h2>{bug.issueTitle || bug.title || 'Untitled Bug'}</h2>
            <div className="bug-meta">
              <span className="bug-id">ID: {bug.ticketId}</span>
              <span className={`status-badge status-${bug.status?.toLowerCase() || 'open'}`}>
                {bug.status || 'Open'}
              </span>
              <span className={`priority-badge priority-${bug.priority?.toLowerCase() || 'medium'}`}>
                {bug.priority || 'Medium'}
              </span>
            </div>
          </div>
          
          <div className="bug-description">
            <h3>Description</h3>
            <div className="description-content">
              <p>{bug.description || bug.issueDescription || 'No description provided'}</p>
            </div>
          </div>

          {bug.errorMessage && (
            <div className="bug-error-message">
              <h3>Error Message</h3>
              <p>{bug.errorMessage}</p>
            </div>
          )}

          {bug.expectedVsActual && (
            <div className="bug-expected-vs-actual">
              <h3>Expected vs Actual</h3>
              <p>{bug.expectedVsActual}</p>
            </div>
          )}

          {bug.stepsToReproduce && bug.stepsToReproduce.length > 0 && (
            <div className="bug-steps-to-reproduce">
              <h3>Steps to Reproduce</h3>
              <ol>
                {bug.stepsToReproduce.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          {(() => {
            // Always show attachment section, but handle different cases
            if (!bug.attachmentData && !bug.attachment) {
              return (
                <div className="bug-attachments">
                  <h3>Attachments</h3>
                  <div className="attachments-list">
                    <div className="attachment">
                      <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        No attachments for this bug report
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
                         const imageUrl = getAbsoluteUrl(bug.attachmentData?.compressedUrl || bug.attachmentData?.originalUrl || bug.attachment);
            if (!imageUrl) {
              console.log('No valid image URL found, not showing attachment section');
              return (
                <div className="bug-attachments">
                  <h3>Attachments</h3>
                  <div className="attachments-list">
                    <div className="attachment">
                      <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        Attachment file not found or invalid
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
            return (
              <div className="bug-attachments">
                <h3>Attachments (1)</h3>
                <div className="attachments-list">
                  <div className="attachment">
                    <img 
                      src={imageUrl}
                      alt="Attachment"
                      className="attachment-image"
                      onClick={() => {
                        if (!imageUrl) {
                          console.log('No valid image URL found, cannot open image viewer');
                          return;
                        }
                        console.log('Opening image viewer for attachment:', bug.attachmentData || bug.attachment);
                        console.log('Image URL:', imageUrl);
                        setSelectedImageIndex(0);
                        setShowImageViewer(true);
                      }}
                      style={{ cursor: 'pointer' }}
                      onError={(e) => {
                        console.error('Failed to load image:', imageUrl);
                        e.target.style.display = 'none';
                        // Add a fallback message
                        const fallbackDiv = document.createElement('div');
                        fallbackDiv.innerHTML = '<p style="padding: 20px; text-align: center; color: #666;">Image not available</p>';
                        e.target.parentNode.appendChild(fallbackDiv);
                      }}
                    />
                 </div>
               </div>
             </div>
           );
         })()}

          <div className="bug-reporter-info">
            <h3>Bug Information</h3>
            <div className="reporter-details">
              <div><strong>Reported by:</strong> {bug.reporterName || bug.name || 'Unknown'}</div>
              <div><strong>Email:</strong> {bug.email || 'No email provided'}</div>
              <div><strong>Department:</strong> {bug.department || 'Not specified'}</div>
              <div><strong>Module:</strong> {bug.module || 'Not specified'}</div>
              <div><strong>Reported on:</strong> {bug.timestamp ? new Date(bug.timestamp).toLocaleString() : 'Unknown date'}</div>
              {bug.lastActivity && (
                <div><strong>Last updated:</strong> {new Date(bug.lastActivity).toLocaleString()}</div>
              )}
              
            </div>
          </div>
        </div>

        <div className="bug-comments-section">
          <h3>Comments</h3>
          <BugComments bugId={bug._id} />
        </div>
      </div>
      
      {showImageViewer && bug && (() => {
        const imageUrl = getAbsoluteUrl(bug.attachmentData?.compressedUrl || bug.attachmentData?.originalUrl || bug.attachment);
        const images = imageUrl ? [{ url: imageUrl }] : [];
        
        console.log('ImageViewer render check:', {
          showImageViewer,
          hasAttachmentData: !!bug.attachmentData,
          hasAttachment: !!bug.attachment,
          imageUrl,
          imagesLength: images.length,
          images
        });
        
        if (images.length === 0) {
          console.log('No valid image URL found, not rendering ImageViewer');
          // Reset the showImageViewer state since there are no valid images
          setShowImageViewer(false);
          return null;
        }
        
        console.log('Rendering ImageViewer with images:', images);
        
        // Double-check that we have valid images before rendering
        if (!images || images.length === 0) {
          console.log('No valid images array, not rendering ImageViewer');
          setShowImageViewer(false);
          return null;
        }
        
        return (
          <ImageViewer 
            images={images}
            onClose={() => {
              console.log('Closing image viewer');
              setShowImageViewer(false);
            }}
          />
        );
      })()}
    </div>
  );
}

// BugComments Component
function BugComments({ bugId }) {
  const { token } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || 'https://intranet.nesimltd.co.ke';

  useEffect(() => {
    fetchComments();
  }, [bugId]);

  const fetchComments = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newComment.trim()
        })
      });
      
      if (response.ok) {
        setNewComment('');
        await fetchComments();
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentCommentId) => {
    if (!replyContent.trim()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: replyContent.trim(),
          parentCommentId: parentCommentId
        })
      });
      
      if (response.ok) {
        setReplyContent('');
        setReplyTo(null);
        await fetchComments();
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderComment = (comment, level = 0) => (
    <div key={comment._id} className={`bug-comment ${level > 0 ? 'nested' : ''}`}>
      <div className="comment-header">
        <div className="comment-author">
          {comment.authorName || comment.authorEmail || 'Unknown User'}
        </div>
        <div className="comment-time">
          {new Date(comment.timestamp).toLocaleString()}
        </div>
      </div>
      
      <div className="comment-content">
        <p>{comment.content}</p>
      </div>
      
      <div className="comment-actions">
        <button 
          className="btn-reply"
          onClick={() => setReplyTo(replyTo === comment._id ? null : comment._id)}
        >
          Reply
        </button>
      </div>
      
      {replyTo === comment._id && (
        <div className="reply-form">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write your reply..."
            className="reply-textarea"
          />
          <div className="reply-actions">
            <button 
              className="btn-submit-reply"
              onClick={() => submitReply(comment._id)}
              disabled={submitting || !replyContent.trim()}
            >
              Submit Reply
            </button>
            <button 
              className="btn-cancel-reply"
              onClick={() => {
                setReplyTo(null);
                setReplyContent('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {comment.replies && comment.replies.length > 0 && (
        <div className="comment-replies">
          {comment.replies.map(reply => renderComment(reply, level + 1))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div className="loading">Loading comments...</div>;
  }

  return (
    <div className="bug-comments-container">
      <div className="comment-form">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="comment-textarea"
        />
        <button 
          className="btn-submit-comment"
          onClick={submitComment}
          disabled={submitting || !newComment.trim()}
        >
          Add Comment
        </button>
      </div>
      
      <div className="comments-list">
        {comments.length > 0 ? (
          comments.map(comment => renderComment(comment))
        ) : (
          <div className="no-comments">
            No comments yet. Be the first to comment!
          </div>
        )}
      </div>
    </div>
  );
} 